import React from 'react'
import { Link, useStaticQuery, graphql } from 'gatsby'
import PropTypes from 'prop-types'
import {
  AppBar,
  Box,
  Button,
  Hidden,
  Toolbar,
} from '@material-ui/core'
import * as Accessa from 'accessa'
import { useTranslation } from 'react-i18next'
import { get, first } from 'lodash'
import DarkMode from './DarkMode'
import Logo from './Logo'
import {
  filterByNodeLocale,
  getLocale,
} from '../utils/i18n-helpers'

const assignLinkAttributes = ({
  title: label,
  relativePath,
}) => ({
  label,
  href: relativePath,
})

const getNodesByLocale = (context = {}) =>
  get(context, 'nodes', []).filter(filterByNodeLocale)

const getPages = (context = {}) =>
  get(first(getNodesByLocale(context)), 'pages', [])

export const getRootNav = () => `/${getLocale()}-ca`

const ContactButton = ({ rootNav, ...rest }) => {
  const { t } = useTranslation('labels')
  return (
    <Button
      color="primary"
      variant="contained"
      to={`${rootNav}/${t('contact').toLowerCase()}`}
      component={Link}
      getProps={({ isCurrent }) =>
        isCurrent
          ? {
              style: {
                backgroundColor: '#87101A',
              },
            }
          : {}
      }
      {...rest}
    >
      {t('contact')}
    </Button>
  )
}

ContactButton.propTypes = {
  rootNav: PropTypes.string.isRequired,
}

export const mapContentfulPagesToAccessaNavigation = (
  pages = []
) =>
  pages.map(({ subPages = [], ...rest }) => ({
    ...assignLinkAttributes(rest),
    items: Array.isArray(subPages)
      ? subPages.map(assignLinkAttributes)
      : [],
  }))

const Navigation = ({ children }) => {
  const { t } = useTranslation('labels')

  const {
    allContentfulNavigation,
    allContentfulArchive,
  } = useStaticQuery(graphql`
    {
      allContentfulArchive(
        filter: {
          contentful_id: { eq: "4mWjzTNdnMQdilQzOBIgs5" }
        }
      ) {
        nodes {
          node_locale
          relativePath
          title
        }
      }

      allContentfulNavigation(
        filter: {
          contentful_id: { eq: "1vbpdNuWkHkgc2ZZm0s0BM" }
        }
      ) {
        nodes {
          node_locale
          pages {
            ... on ContentfulArchive {
              title
              relativePath
              node_locale
              subPages {
                title
                relativePath
                node_locale
              }
            }
            ... on ContentfulPage {
              node_locale
              title
              relativePath
            }
          }
        }
      }
    }
  `)

  const rootNav = getRootNav()
  const navItems = mapContentfulPagesToAccessaNavigation([
    ...getPages(allContentfulNavigation),
    {
      title: t('resources'),
      subPages: getNodesByLocale(
        allContentfulArchive
      ).concat({
        title: t('downloads'),
        relativePath: t('downloadsPath'),
      }),
    },
  ])

  return (
    <AppBar
      elevation={0}
      position="absolute"
      color="transparent"
    >
      {children}
      <Toolbar
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          aria-label="index"
          component={Link}
          py={1}
          px={4}
          to={rootNav}
          zIndex={100}
        >
          <Logo />
        </Box>
        <Box>
          <Hidden mdDown>
            <DarkMode>
              <Accessa.Navigation.MenuListComposition
                maxColumns={2}
                items={navItems}
              />
            </DarkMode>
          </Hidden>
        </Box>
        <Box color="background.paper">
          <Hidden lgUp>
            <Accessa.Navigation.Offcanvas
              logo={
                <img
                  src="/full.svg"
                  alt="CRG full logo"
                  style={{
                    marginLeft: '.75rem',
                    width: 170,
                  }}
                />
              }
            >
              <Box
                className="offcanvas-links"
                my={2}
                mx={1}
              >
                <Accessa.Navigation.Tree items={navItems} />
              </Box>
              <Box mx={1}>
                <ContactButton
                  rootNav={rootNav}
                  fullWidth
                />
              </Box>
            </Accessa.Navigation.Offcanvas>
          </Hidden>
          <Hidden mdDown>
            <ContactButton
              rootNav={rootNav}
              style={{ marginLeft: '1rem' }}
            />
          </Hidden>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

Navigation.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
}

Navigation.defaultProps = {}

export default Navigation
